import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';
import { emailToDisplayName } from '../ActionPlan/utils/formOptions';
import { fetchActivities, ListActivitiesParams } from './peopleHubApi';
import { setActivityHistoryLoading, setActivityHistoryData, ActivityHistoryQuery } from '../../store/slices/peopleHubSlice';
import { fromBackendWeek } from '../../modules/dlo/utils/weekTransforms';

// ─── Row shape (raw GET /people/activities ActivityDTO) ──────────────────────
export interface HistoryTabActivityRow {
  id: string;
  employeeName: string;
  employeeId?: string | null;
  employeeRole?: string | null;
  role: string;
  /** Raw activity type: OBSERVATION | PULSE_CHECK | TRAINING | ... */
  type: string;
  /** "Full" | "Routine" | "N/A" — only meaningful for OBSERVATION rows. */
  observationType?: string | null;
  status: string;
}

interface ActivityRow {
  id: string;
  employeeName: string;
  employeeId?: string | null;
  employeeRole?: string | null;
  role: string;
  type: string;
  observationType?: string | null;
  result: string | null;
  createdOn: string | null;
  createdBy: string | null;
  createdByEmail: string | null;
  completedBy: string | null;
  completedByEmail: string | null;
  finalized: string | null;
  period: string | null;
  status: string;
}

interface ColDef {
  key: keyof ActivityRow;
  label: string;
  width: number;
  sortKey: string;
  /** Enables the filter-list icon + multi-select dropdown (client-side, over the loaded page). */
  filterable?: boolean;
}

const ALL_COLUMNS: ColDef[] = [
  { key: 'employeeName', label: 'Employee Name', width: 180, sortKey: 'employeeName' },
  { key: 'role',         label: 'Role',          width: 140, sortKey: 'role',         filterable: true },
  { key: 'type',         label: 'Type',          width: 150, sortKey: 'type',         filterable: true },
  { key: 'result',       label: 'Result',        width: 120, sortKey: 'result',       filterable: true },
  { key: 'createdOn',    label: 'Created On',    width: 130, sortKey: 'createdOn' },
  { key: 'createdBy',    label: 'Created By',    width: 150, sortKey: 'createdBy',    filterable: true },
  { key: 'completedBy',  label: 'Completed By',  width: 150, sortKey: 'completedBy',  filterable: true },
  { key: 'finalized',    label: 'Finalized',     width: 130, sortKey: 'finalized' },
  { key: 'period',       label: 'Period/Week',   width: 120, sortKey: 'period' },
  { key: 'status',       label: 'Status',        width: 130, sortKey: 'status',       filterable: true },
];
const ACTION_COL_WIDTH = 120;
const TABLE_MIN_WIDTH = 1380 + ACTION_COL_WIDTH;
const PAGE_SIZES = [25, 50, 100] as const;
const DEFAULT_SORT_KEY = 'createdOn';

const cellBox = (width: number) => ({ flexGrow: width, flexShrink: 0, flexBasis: width, minWidth: width });

function formatEnumLabel(raw: string | null | undefined): string {
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "Full Observation" / "Routine Observation" for observations; otherwise the enum label. */
function formatTypeLabel(row: Pick<ActivityRow, 'type' | 'observationType'>): string {
  if (row.type === 'OBSERVATION' && (row.observationType === 'Full' || row.observationType === 'Routine')) {
    return `${row.observationType} Observation`;
  }
  return formatEnumLabel(row.type);
}

/** Only pulse checks and observations have an activity screen to resume/view. */
function getRowAction(row: ActivityRow): 'resume' | 'view' | null {
  if (row.type !== 'OBSERVATION' && row.type !== 'PULSE_CHECK') return null;
  if (row.status === 'COMPLETED') return 'view';
  if (row.status === 'IN_PROGRESS' || row.status === 'NEW') return 'resume';
  return null;
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Formats a raw "{year}P{period}W{backendWeek}" period string (e.g. "2026P11W42") as
// "P{period}W{week} {yy}" using fromBackendWeek to recompute the true UI period/week
// from the embedded backend week number.
function formatPeriodWeek(raw: string | null | undefined): string {
  if (!raw) return '—';
  const match = /^(\d{4})P\d+W(\d+)$/.exec(raw);
  if (!match) return raw;
  const [, yearStr, backendWeekStr] = match;
  const { period, week } = fromBackendWeek(Number(backendWeekStr));
  return `P${period}W${week} ${yearStr.slice(-2)}`;
}

function typeColorsFor(type: string): { bg: string; fg: string } {
  if (type === 'OBSERVATION') return { bg: '#005CBC', fg: '#FFFFFF' };
  if (type === 'PULSE_CHECK') return { bg: '#0078A7', fg: '#FFFFFF' };
  if (type === 'MILESTONE_REVIEW') return { bg: '#4D2E0C', fg: '#FFFFFF' };
  if (type === 'CERTIFICATION') return { bg: '#0C420B', fg: '#FFFFFF' };
  return { bg: '#585858', fg: '#FFFFFF' };
}

function statusColorsFor(status: string): { bg: string; fg: string } {
  if (status === 'COMPLETED') return { bg: '#CAF3C6', fg: '#0C420B' };
  if (status === 'IN_PROGRESS') return { bg: '#DAEEFA', fg: '#034895' };
  if (status === 'MISSED') return { bg: '#FBE6E9', fg: '#A10D26' };
  return { bg: '#E6E6E6', fg: '#585858' };
}

function Pill({ label, colors }: { label: string; colors: { bg: string; fg: string } }) {
  return (
    <View style={[pillStyles.wrap, { backgroundColor: colors.bg }]}>
      <Text style={[pillStyles.text, { color: colors.fg }]} numberOfLines={1}>{label}</Text>
    </View>
  );
}
const pillStyles = StyleSheet.create({
  wrap: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, alignSelf: 'flex-start' },
  text: { fontSize: 11, lineHeight: 16, fontFamily: FontFamily.bold, fontWeight: '700' },
});

// ─── Sortable column header + Modal-based multi-select filter (drives the server request) ───
interface FilterOption { value: string; label: string; }
const ColumnHeader: React.FC<{
  col: ColDef;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (col: ColDef) => void;
  options: FilterOption[];
  selected: Set<string>;
  onFilterChange: (next: Set<string>) => void;
  openFilterKey: string | null;
  onFilterToggle: (key: string | null) => void;
}> = ({ col, sortBy, sortOrder, onSort, options, selected, onFilterChange, openFilterKey, onFilterToggle }) => {
  const active = sortBy === col.sortKey;
  const triggerRef = useRef<View>(null);
  const [menuLayout, setMenuLayout] = useState({ top: 0, left: 0 });
  const isOpen = openFilterKey === col.key;

  const openMenu = () => {
    triggerRef.current?.measureInWindow((x, y, _w, h) => {
      setMenuLayout({ top: y + h + 4, left: x });
      onFilterToggle(col.key);
    });
  };

  return (
    <View style={[chStyles.cell, cellBox(col.width)]}>
      <TouchableOpacity style={chStyles.labelRow} onPress={() => onSort(col)} activeOpacity={0.7}>
        <Text style={[chStyles.label, active && chStyles.labelActive]}>{col.label}</Text>
        <MaterialIcons
          name={active && sortOrder === 'DESC' ? 'arrow-downward' : 'arrow-upward'}
          size={12}
          color={active ? AdminColors.blue700 : AdminColors.gray350}
        />
      </TouchableOpacity>
      {!!col.filterable && (
        <View ref={triggerRef}>
          <TouchableOpacity onPress={openMenu} hitSlop={6} style={chStyles.filterBtn}>
            <MaterialIcons
              name="filter-list"
              size={16}
              color={selected.size > 0 ? AdminColors.blue700 : AdminColors.gray350}
            />
          </TouchableOpacity>
        </View>
      )}

      <Modal visible={isOpen} transparent animationType="none" onRequestClose={() => onFilterToggle(null)}>
        <Pressable style={StyleSheet.absoluteFill} onPress={() => onFilterToggle(null)}>
          <Pressable
            style={[chStyles.filterMenu, { top: menuLayout.top, left: menuLayout.left }]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={chStyles.filterActionsRow}>
              <TouchableOpacity onPress={() => onFilterChange(new Set(options.map((o) => o.value)))}>
                <Text style={chStyles.filterActionText}>Select All</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onFilterChange(new Set())}>
                <Text style={chStyles.filterActionText}>Clear</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
              {options.length === 0 && (
                <Text style={chStyles.filterEmptyText}>No options available</Text>
              )}
              {options.map((opt) => {
                const checked = selected.has(opt.value);
                return (
                  <TouchableOpacity
                    key={opt.value}
                    style={chStyles.filterOption}
                    onPress={() => {
                      const next = new Set(selected);
                      checked ? next.delete(opt.value) : next.add(opt.value);
                      onFilterChange(next);
                    }}
                  >
                    <View style={[chStyles.checkbox, checked && chStyles.checkboxChecked]}>
                      {checked && <Text style={chStyles.checkboxTick}>✓</Text>}
                    </View>
                    <Text style={chStyles.filterOptionText} numberOfLines={1}>{opt.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};
const chStyles = StyleSheet.create({
  cell: { flexDirection: 'row', alignItems: 'center', gap: 2, paddingHorizontal: 12, paddingVertical: 11 },
  labelRow: { flexDirection: 'row', alignItems: 'center', gap: 3, minHeight: 24 },
  label: {
    fontSize: 10, lineHeight: 14, fontWeight: '700', color: AdminColors.textSecondary, fontFamily: FontFamily.bold,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  labelActive: { color: AdminColors.blue700 },
  filterBtn: { padding: 2 },
  filterMenu: {
    position: 'absolute', backgroundColor: AdminColors.bgSurface, borderWidth: 1, borderColor: AdminColors.borderDefault,
    borderRadius: 8, width: 220, paddingVertical: 8, paddingHorizontal: 8, gap: 4,
    shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.15, shadowRadius: 16, elevation: 8,
  },
  filterActionsRow: { flexDirection: 'row', gap: 14, paddingHorizontal: 4, paddingBottom: 6 },
  filterActionText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },
  filterOption: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 4 },
  filterEmptyText: { fontSize: 12, fontFamily: FontFamily.regular, color: AdminColors.textMuted, padding: 8 },
  checkbox: {
    width: 16, height: 16, borderRadius: 3, borderWidth: 1, borderColor: AdminColors.borderDefault,
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxChecked: { backgroundColor: AdminColors.blue700, borderColor: AdminColors.blue700 },
  checkboxTick: { color: '#FFFFFF', fontSize: 10 },
  filterOptionText: { fontSize: 12, fontFamily: FontFamily.regular, color: AdminColors.textPrimary, flexShrink: 1 },
});

// ─── Main component ───────────────────────────────────────────────────────────
interface PeopleHubHistoryProps {
  /** Opens the Employee Profile screen for the clicked Employee Name cell. */
  onOpenEmployee?: (employeeName: string, employeeId?: string, employeeRole?: string) => void;
  /** Re-opens the activity screen (RESUME call) for the clicked Resume / View button. */
  onOpenActivity?: (row: HistoryTabActivityRow) => void | Promise<void>;
}

const PeopleHubHistory: React.FC<PeopleHubHistoryProps> = ({ onOpenEmployee, onOpenActivity }) => {
  const [openingRowId, setOpeningRowId] = useState<string | null>(null);

  const handleActionPress = async (row: ActivityRow) => {
    setOpeningRowId(row.id);
    try {
      await onOpenActivity?.(row);
    } finally {
      setOpeningRowId(null);
    }
  };
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const locationName: string | undefined = selectedLocation?.name ?? selectedLocation?.displayText;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_KEY);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<25 | 50 | 100>(25);

  const dispatch = useDispatch();
  const activityHistoryCache = useSelector((state: any) => state.peopleHub?.activityHistory);

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  // Column -> ListActivitiesParams key the selected raw values are sent under.
  const FILTER_PARAM_KEYS: Record<string, keyof ListActivitiesParams> = {
    role: 'roles', type: 'types', result: 'results', status: 'statuses',
    createdBy: 'createdByEmails', completedBy: 'completedByEmails',
  };

  // Serialized (sorted) filter snapshot — stable across renders, used as both the
  // effect dependency and the cache-key field so the API is only re-hit when the
  // actual selected values change.
  const filtersSnapshot = useMemo(() => {
    const snapshot: Record<string, string[]> = {};
    Object.entries(colFilters).forEach(([key, set]) => {
      if (set.size > 0) snapshot[key] = [...set].sort();
    });
    return snapshot;
  }, [colFilters]);
  const filtersKey = JSON.stringify(filtersSnapshot);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!locationName) return;
    const query: ActivityHistoryQuery = {
      locationName,
      search: debouncedSearch,
      sortBy, sortOrder, page, size,
      filters: filtersSnapshot,
    };

    // Reuse the Home tab's cached response when this is the same default query
    // (no need to hit the API again for the view the user just came from).
    const cached = activityHistoryCache;
    const cachedQuery = cached?.query as ActivityHistoryQuery | null | undefined;
    const isCacheHit =
      cached?.loadingState === 'succeeded' &&
      !!cachedQuery &&
      cachedQuery.locationName === query.locationName &&
      cachedQuery.search === query.search &&
      cachedQuery.sortBy === query.sortBy &&
      cachedQuery.sortOrder === query.sortOrder &&
      cachedQuery.page === query.page &&
      cachedQuery.size === query.size &&
      JSON.stringify(cachedQuery.filters ?? {}) === filtersKey;

    if (isCacheHit) {
      setRows(cached.rows);
      setTotal(cached.total);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      dispatch(setActivityHistoryLoading());
      try {
        const params: ListActivitiesParams = {
          locationName,
          search: debouncedSearch || undefined,
          sortBy, sortOrder, page, size,
        };
        Object.entries(filtersSnapshot).forEach(([key, values]) => {
          const paramKey = FILTER_PARAM_KEYS[key];
          if (paramKey) (params as any)[paramKey] = values;
        });
        const res = await fetchActivities(params);
        if (cancelled) return;
        const fetchedRows = res?.activities ?? [];
        const fetchedTotal = res?.pagination?.total ?? fetchedRows.length;
        setRows(fetchedRows);
        setTotal(fetchedTotal);
        dispatch(setActivityHistoryData({ query, rows: fetchedRows, total: fetchedTotal }));
      } catch (err) {
        console.warn('Failed to load All History activities', err);
        if (!cancelled) { setRows([]); setTotal(0); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName, debouncedSearch, sortBy, sortOrder, page, size, filtersKey]);

  const handleSort = (col: ColDef) => {
    if (sortBy !== col.sortKey) {
      setSortBy(col.sortKey);
      setSortOrder('ASC');
    } else if (sortOrder === 'ASC') {
      setSortOrder('DESC');
    } else {
      setSortBy(DEFAULT_SORT_KEY);
      setSortOrder('DESC');
    }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / size));

  const filterOptions = useMemo(() => {
    const opts: Record<string, FilterOption[]> = {};
    ALL_COLUMNS.filter((c) => c.filterable).forEach((col) => {
      const map = new Map<string, string>();
      rows.forEach((row) => {
        let raw: string | null | undefined;
        let label: string;
        if (col.key === 'createdBy') {
          raw = row.createdByEmail || row.createdBy;
          label = row.createdByEmail ? emailToDisplayName(row.createdByEmail) : (row.createdBy || '—');
        } else if (col.key === 'completedBy') {
          raw = row.completedByEmail || row.completedBy;
          label = row.completedByEmail ? emailToDisplayName(row.completedByEmail) : (row.completedBy || '—');
        } else if (col.key === 'type') {
          raw = row.type; label = formatEnumLabel(row.type);
        } else if (col.key === 'status') {
          raw = row.status; label = formatEnumLabel(row.status);
        } else if (col.key === 'result') {
          raw = row.result; label = row.result ? formatEnumLabel(row.result) : '—';
        } else {
          raw = String(row[col.key] ?? ''); label = raw || '—';
        }
        if (raw) map.set(raw, label);
      });
      opts[col.key] = [...map.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });
    return opts;
  }, [rows]);

  const handleFilterToggle = (key: string | null) => setOpenFilterKey((prev) => (prev === key ? null : key));
  const handleFilterChange = (key: string, next: Set<string>) => {
    setColFilters((f) => ({ ...f, [key]: next }));
    setPage(1);
  };

  const renderCell = (row: ActivityRow, col: ColDef) => {
    if (col.key === 'type') return <Pill label={formatTypeLabel(row)} colors={typeColorsFor(row.type)} />;
    if (col.key === 'status') return <Pill label={formatEnumLabel(row.status)} colors={statusColorsFor(row.status)} />;
    if (col.key === 'result') return <Text style={styles.cellText}>{row.result ? formatEnumLabel(row.result) : '—'}</Text>;
    if (col.key === 'createdOn' || col.key === 'finalized') return <Text style={styles.cellText}>{formatShortDate(row[col.key] as string | null)}</Text>;
    if (col.key === 'createdBy') {
      return <Text style={styles.cellText} numberOfLines={1}>{row.createdByEmail ? emailToDisplayName(row.createdByEmail) : (row.createdBy || '—')}</Text>;
    }
    if (col.key === 'completedBy') {
      return <Text style={styles.cellText} numberOfLines={1}>{row.completedByEmail ? emailToDisplayName(row.completedByEmail) : (row.completedBy || '—')}</Text>;
    }
    if (col.key === 'employeeName') {
      return (
        <Pressable
          onPress={() => onOpenEmployee?.(row.employeeName, row.employeeId ?? undefined, row.employeeRole ?? undefined)}
          hitSlop={4}
        >
          <Text style={styles.nameCell} numberOfLines={1}>{row.employeeName}</Text>
        </Pressable>
      );
    }
    if (col.key === 'period') return <Text style={styles.cellText}>{formatPeriodWeek(row.period)}</Text>;
    const value = row[col.key];
    return <Text style={styles.cellText} numberOfLines={1}>{value ? String(value) : '—'}</Text>;
  };

  return (
    <View style={styles.page}>
      <View style={styles.titleBlock}>
        <Text style={styles.title}>All History</Text>
        <Text style={styles.subtitle}>
          Every pulse check, observation, onboarding, training, and certification activity recorded at this
          location, including assignments that closed as Missed. Sort and filter directly from every data column.
        </Text>
      </View>

      <View style={styles.toolbar}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={16} color={AdminColors.gray350} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search employee, activity, author, status, or result"
            placeholderTextColor={AdminColors.gray350}
            value={searchInput}
            onChangeText={setSearchInput}
          />
        </View>
        <Text style={styles.countText}>Showing {rows.length} of {total} records</Text>
        {isLoading && <ActivityIndicator size="small" color={AdminColors.blue700} />}
      </View>

      <View style={styles.tableWrap}>
        <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.tableScrollContent}>
          <View style={styles.table}>
            <View style={styles.theadRow}>
              {ALL_COLUMNS.map((col) => (
                <ColumnHeader
                  key={col.key}
                  col={col}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  options={filterOptions[col.key] ?? []}
                  selected={colFilters[col.key] ?? new Set()}
                  onFilterChange={(next) => handleFilterChange(col.key, next)}
                  openFilterKey={openFilterKey}
                  onFilterToggle={handleFilterToggle}
                />
              ))}
              <View style={cellBox(ACTION_COL_WIDTH)} />
            </View>

            {!isLoading && rows.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>No activity records match the selected filters.</Text>
              </View>
            )}

            {rows.map((row, idx) => (
              <View
                key={row.id}
                style={[styles.row, idx === rows.length - 1 && styles.rowLast]}
              >
                {ALL_COLUMNS.map((col) => (
                  <View key={col.key} style={[styles.cell, cellBox(col.width)]}>
                    {renderCell(row, col)}
                  </View>
                ))}
                <View style={[styles.cell, styles.actionCell, cellBox(ACTION_COL_WIDTH)]}>
                  {(() => {
                    const action = getRowAction(row);
                    if (!action) return null;
                    if (openingRowId === row.id) return <ActivityIndicator size="small" color={AdminColors.blue700} />;
                    const isResume = action === 'resume';
                    return (
                      <Pressable
                        style={[styles.actionButton, isResume && styles.actionButtonPrimary]}
                        onPress={() => handleActionPress(row)}
                        disabled={openingRowId !== null}
                        accessibilityLabel={`${isResume ? 'Resume' : 'View'} ${formatTypeLabel(row)} for ${row.employeeName}`}
                      >
                        <Text style={[styles.actionButtonText, isResume && styles.actionButtonTextPrimary]}>
                          {isResume ? 'Resume' : 'View'}
                        </Text>
                      </Pressable>
                    );
                  })()}
                </View>
              </View>
            ))}
          </View>
        </ScrollView>
      </View>

      <View style={styles.pagingRow}>
        <Text style={styles.paginationInfo}>
          {total === 0
            ? 'Showing 0 of 0'
            : `Showing ${Math.min((page - 1) * size + 1, total)}–${Math.min(page * size, total)} of ${total}`}
        </Text>
        <View style={styles.paginationControls}>
          <TouchableOpacity
            style={[styles.paginationButton, page <= 1 && styles.paginationButtonDisabled]}
            disabled={page <= 1}
            onPress={() => setPage((p) => Math.max(1, p - 1))}
            activeOpacity={0.7}
            accessibilityLabel="Previous page"
          >
            <MaterialIcons name="chevron-left" size={20} color={page <= 1 ? '#CED4DA' : '#005CBC'} />
          </TouchableOpacity>
          <Text style={styles.paginationPageText}>{`Page ${page} of ${totalPages}`}</Text>
          <TouchableOpacity
            style={[styles.paginationButton, page >= totalPages && styles.paginationButtonDisabled]}
            disabled={page >= totalPages}
            onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
            activeOpacity={0.7}
            accessibilityLabel="Next page"
          >
            <MaterialIcons name="chevron-right" size={20} color={page >= totalPages ? '#CED4DA' : '#005CBC'} />
          </TouchableOpacity>
        </View>
        <View style={styles.paginationSizeSelector}>
          {PAGE_SIZES.map((opt) => (
            <TouchableOpacity
              key={opt}
              style={[styles.paginationSizeOption, size === opt && styles.paginationSizeOptionActive]}
              onPress={() => { setSize(opt); setPage(1); }}
              activeOpacity={0.7}
            >
              <Text style={[styles.paginationSizeText, size === opt && styles.paginationSizeTextActive]}>{opt}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: AdminColors.bgPage, padding: 16, gap: 16 },
  titleBlock: { gap: 3 },
  title: { fontSize: 18, lineHeight: 24, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.textPrimary },
  subtitle: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textSecondary },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  searchWrap: {
    flexGrow: 1, flexBasis: 360, maxWidth: 620, flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6,
    paddingHorizontal: 10, height: 36, backgroundColor: AdminColors.bgSurface,
  },
  searchInput: { flex: 1, fontSize: 14, color: AdminColors.textPrimary, fontFamily: FontFamily.regular, height: 34, padding: 0 },
  countText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.blue800, marginLeft: 'auto' },

  tableWrap: {
    width: '100%', backgroundColor: AdminColors.bgSurface,
    borderWidth: 1, borderColor: AdminColors.gray25, borderRadius: 10, overflow: 'hidden',
  },
  tableScrollContent: { minWidth: '100%' },
  table: { minWidth: TABLE_MIN_WIDTH, width: '100%' },
  theadRow: {
    flexDirection: 'row', alignItems: 'center',
    borderBottomWidth: 2, borderBottomColor: AdminColors.borderTable, backgroundColor: AdminColors.bgSurface,
  },

  row: {
    flexDirection: 'row', alignItems: 'center',
    borderBottomWidth: 1, borderBottomColor: AdminColors.gray25,
  },
  rowLast: { borderBottomWidth: 0 },
  cell: { paddingHorizontal: 12, paddingVertical: 11, justifyContent: 'center' },
  actionCell: { alignItems: 'flex-end' },
  actionButton: {
    borderWidth: 1.5, borderColor: AdminColors.blue900, borderRadius: 16,
    paddingVertical: 6, paddingHorizontal: 18, backgroundColor: AdminColors.bgSurface,
  },
  actionButtonPrimary: { backgroundColor: AdminColors.blue900 },
  actionButtonText: { fontFamily: FontFamily.bold, fontWeight: '700', fontSize: 13, color: AdminColors.blue900 },
  actionButtonTextPrimary: { color: AdminColors.textOnDark },
  cellText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textPrimary },
  nameCell: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.textLink },

  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: 28 },
  emptyText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textSecondary },

  pagingRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
  paginationInfo: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textSecondary },
  paginationControls: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  paginationButton: {
    width: 28, height: 28, borderRadius: 4, borderWidth: 1, borderColor: '#DEE2E6',
    alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF',
  },
  paginationButtonDisabled: { backgroundColor: '#F8F9FA', borderColor: '#F0F0F0' },
  paginationPageText: { fontFamily: FontFamily.regular, fontSize: 12, color: '#2D2D2D', minWidth: 90, textAlign: 'center' },
  paginationSizeSelector: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  paginationSizeOption: {
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, borderWidth: 1, borderColor: '#DEE2E6', backgroundColor: '#FFFFFF',
  },
  paginationSizeOptionActive: { borderColor: '#005CBC', backgroundColor: '#EBF3FF' },
  paginationSizeText: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textSecondary },
  paginationSizeTextActive: { fontFamily: FontFamily.bold, color: '#005CBC' },
});

export default PeopleHubHistory;
